import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// @ts-ignore-next-line importsNotUsedAsValues
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import isEmpty from 'lodash/fp/isEmpty';
import noop from 'lodash/fp/noop';
import { useDropDirection } from '../../utils/useDropDirection';
import { DOWN, scrollItemIntoView, UP } from '../../utils/scrollItemIntoView';
import useEffectOnce from '../../hooks/useEffectOnce';
import useKey from '../../useKey';
import DropdownHeader from './DropdownHeader';
import NoItemMessage from './NoItemMessage';
import Spinner from '../spinner/Spinner';
const DATA_ATTRIBUTE_ID = 'data-item-id';
const DEFAULT_FOCUSED_ITEM_INDEX = 0;
const HIGHLIGHT_CLASS = 'focus';
const BaseSelectDropdown = (props) => {
    const { isOpen = false, isLoading = false, updateDOMValues = noop, onOpen = noop, onSelect = noop, onClose = noop, options = [], autoDropDirection = true, dropup = false, pullRight = false, useActiveClass = false, focusedItemIndex: externalFocusedItemIndex, keyboardUsed: externalKeyboardUsed, noItemMessage, dropdownClassName, } = props;
    const [focusedItemIndex, setFocusedItemIndex] = useState(externalFocusedItemIndex || DEFAULT_FOCUSED_ITEM_INDEX);
    const [keyboardUsed, setKeyboardUsed] = useState(externalKeyboardUsed);
    const dropdownMenuRef = useRef(null);
    useEffectOnce(() => {
        // all available items need to be rendered in order to know their DOM value
        // which will be used for filtering in the parent component
        const currentItemDOMValues = updateItemDOMValues();
        updateDOMValues(currentItemDOMValues);
    });
    // Overwrite position of dropdown menu in case auto drop is enabled
    const dropDirection = useDropDirection({ pullRight, dropup, autoDropDirection, dropdownMenuRef }, [isOpen]);
    // Add or remove the "dropup" class from the parent Select/Multiselect component to position
    // the dropdown element accordingly via CSS
    useLayoutEffect(() => {
        if (dropdownMenuRef.current) {
            const parent = dropdownMenuRef.current.parentElement;
            if (dropDirection.dropup) {
                parent === null || parent === void 0 ? void 0 : parent.classList.add('dropup');
            }
            else {
                parent === null || parent === void 0 ? void 0 : parent.classList.remove('dropup');
            }
        }
    }, [dropDirection, dropdownMenuRef]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (dropdownMenuRef.current) {
            updateDOMValues(updateItemDOMValues());
        }
    }, [dropdownMenuRef.current]);
    // update internal state for isOpen
    const [previousIsOpen, setPreviousIsOpen] = useState(isOpen);
    if (isOpen && !previousIsOpen) {
        onOpen(dropDirection.dropup);
        setPreviousIsOpen(isOpen);
    }
    else if (!isOpen && previousIsOpen) {
        onClose();
        setPreviousIsOpen(isOpen);
    }
    useKey(event => {
        if (isOpen) {
            switch (event.key) {
                case 'Escape': {
                    // close dropdown on esc
                    onClose();
                    break;
                }
                case 'Tab': {
                    // close dropdown on tab
                    onClose();
                    break;
                }
                case 'Enter': {
                    // select item on enter
                    selectOptionOnEnter(event);
                    break;
                }
                case 'ArrowUp': {
                    // prevent scrolling the page when dropdown menu is open
                    event.preventDefault();
                    // select item above on arrow up key
                    focusOption(UP);
                    scrollItemIntoView(UP, dropdownMenuRef.current, getFocusedOptionNode());
                    break;
                }
                case 'ArrowDown': {
                    // prevent scrolling the page when dropdown menu is open
                    event.preventDefault();
                    // select item below on arrow down key
                    focusOption(DOWN);
                    scrollItemIntoView(DOWN, dropdownMenuRef.current, getFocusedOptionNode());
                    break;
                }
                default:
                    break;
            }
        }
    });
    const focusOption = (direction) => {
        let nextFocusedItem = 0;
        switch (direction) {
            case UP: {
                nextFocusedItem = focusedItemIndex <= 0 ? focusedItemIndex : focusedItemIndex - 1;
                break;
            }
            case DOWN: {
                nextFocusedItem = focusedItemIndex === options.length - 1 ? focusedItemIndex : focusedItemIndex + 1;
                break;
            }
            default:
                break;
        }
        // In case the next item index is negative, means outside the bounds of the items,
        // reset it depending on the current direction
        const indexLimit = direction === UP ? options.length - 1 : 0;
        setFocusedItemIndex(nextFocusedItem < 0 ? indexLimit : nextFocusedItem);
        setKeyboardUsed(true);
    };
    const getOptionNodes = () => {
        const node = dropdownMenuRef.current;
        return (node === null || node === void 0 ? void 0 : node.getElementsByTagName('a')) || [];
    };
    const updateItemDOMValues = () => {
        if (dropdownMenuRef.current) {
            const optionNodes = getOptionNodes();
            return [...optionNodes].map(item => {
                return {
                    id: item.getAttribute(DATA_ATTRIBUTE_ID),
                    text: item.textContent,
                };
            });
        }
    };
    const getFocusedOptionNode = () => {
        const optionNodes = getOptionNodes();
        return [...optionNodes].find(item => item.className.includes(HIGHLIGHT_CLASS));
    };
    const selectOptionOnEnter = (event) => {
        event.preventDefault();
        // When no filter result was found, avoid selecting anything
        if (isEmpty(options)) {
            return;
        }
        const match = getFocusedOptionNode();
        if (match) {
            const selectedItem = options.find(option => option.id === match.getAttribute(DATA_ATTRIBUTE_ID));
            onSelect(selectedItem);
        }
    };
    const handleOptionChange = (event) => {
        event.preventDefault();
        const optionId = event.currentTarget.getElementsByTagName('input')[0].value;
        const selectedItem = options.find(option => option.id === optionId);
        onSelect(selectedItem);
    };
    const dropdownMenuClasses = classNames('dropdown-menu', dropDirection.pullRight && 'pull-right', dropdownClassName);
    // Don't show the dropdown, when no match are found when filtering unless there is a not found message
    if (isEmpty(options)) {
        return _jsx(NoItemMessage, { noItemMessage: noItemMessage, className: dropdownMenuClasses });
    }
    return (_jsxs("ul", Object.assign({ className: dropdownMenuClasses, ref: dropdownMenuRef, role: 'menu' }, { children: [isLoading && (_jsx("div", Object.assign({ className: 'display-flex justify-content-center padding-10' }, { children: _jsx(Spinner, {}) }))), !isLoading &&
                options.map((option, index) => {
                    if (option.header) {
                        return _jsx(DropdownHeader, { icon: option.icon, label: option.label }, option.id);
                    }
                    // Show focused style only when keyboard is in use
                    const anchorClassNames = classNames('display-flex align-items-center', keyboardUsed && focusedItemIndex === index ? HIGHLIGHT_CLASS : '', option.disabled && 'pointer-events-none');
                    const wrapperClassNames = classNames(option.disabled && 'disabled', useActiveClass && option.selected && 'active');
                    return (_jsx("li", Object.assign({ className: wrapperClassNames, role: 'listitem' }, { children: _jsxs("a", Object.assign({ role: 'menuitem', className: anchorClassNames, "data-item-id": option.id, "data-item-index": index, 
                            // Note, we need to assign the click callback only when it's not disabled
                            // otherwise the functions is still triggered
                            // biome-ignore lint/a11y/useValidAnchor: due to old structure + backwards compatibility
                            onClick: option.disabled ? undefined : handleOptionChange }, { children: [_jsxs("span", { children: [option.icon && _jsx("span", Object.assign({ className: 'margin-right-5' }, { children: option.icon })), option.label] }), _jsx("input", { type: 'hidden', value: option.id })] })) }), option.id));
                })] })));
};
export const filterOptions = (itemDOMValues, filterValue, options) => {
    const filteredDOMValues = itemDOMValues.filter(item => item.text.toLowerCase().includes(filterValue.toLowerCase()));
    // Filter the options according to the filtered DOM values and map the IDs since the filter cannot be done
    // on the options itself as they might contain arbitrary components
    return options.filter(option => {
        return filteredDOMValues.find(domValue => domValue.id === option.id);
    });
};
export default BaseSelectDropdown;
